import {
  Checkbox,
  getFontCss,
  styleConstants,
  themeConstants,
} from '@paywithextend/components'
import React from 'react'
import { useSelector } from 'react-redux'
import DividerLineDiv from 'src/components/DividerLineDiv/DividerLineDiv'
import { useCurrentUser } from 'src/context/UserProvider/useUser'
import { VirtualCardTypes } from 'src/enums'
import { useTranslate } from 'src/localization/Trns'
import { useQueryOrganizationVirtualCardTypes } from 'src/queries/organizations/virtualCardTypes/virtualCardTypes.queries'
import { selectBrandFeaturesFromState } from 'src/redux/selectors/session.selectors'
import { css } from 'styled-components'

interface VirtualCardTypeFilterProps {
  selectedCardTypes?: Set<string>
  onToggleCardType: (cardType: string) => void
}

const VirtualCardTypeFilter: React.FC<VirtualCardTypeFilterProps> = ({
  selectedCardTypes,
  onToggleCardType,
}): React.ReactElement | null => {
  const translate = useTranslate()
  const { recurringVirtualCardsEnabled } = useSelector(
    selectBrandFeaturesFromState,
  )
  const user = useCurrentUser()

  const { data: virtualCardTypes } = useQueryOrganizationVirtualCardTypes({
    orgId: user?.organizationId,
    enabled: user?.organizationId !== undefined,
  })

  const showStandard = virtualCardTypes?.includes(VirtualCardTypes.standard)
  const showRecurring =
    virtualCardTypes?.includes(VirtualCardTypes.recurring) &&
    recurringVirtualCardsEnabled
  const showGuestCard = virtualCardTypes?.includes(VirtualCardTypes.guestAccess)
  const showGuestRecurring =
    virtualCardTypes?.includes(VirtualCardTypes.recurringGuest) &&
    recurringVirtualCardsEnabled
  const showDecliningBalance = virtualCardTypes?.includes(
    VirtualCardTypes.decliningBalance,
  )
  const showIssuerCycle = virtualCardTypes?.includes(
    VirtualCardTypes.issuerCycle,
  )

  if (
    !showStandard &&
    !showRecurring &&
    !showGuestCard &&
    !showGuestRecurring &&
    !showDecliningBalance &&
    !showIssuerCycle
  ) {
    return null
  }

  return (
    <>
      <div
        role="group"
        aria-labelledby="filter-card-type"
        css={css`
          display: flex;
          flex-direction: column;
          gap: 12px;
        `}
      >
        <div
          id="filter-card-type"
          css={css`
            ${getFontCss(
              14,
              themeConstants.colors.font_primary,
              styleConstants.fontFamilies.medium,
            )};
          `}
        >
          {translate('Card Type')}
        </div>
        {showStandard && (
          <Checkbox
            label={translate('Standard')}
            testId="standardChk"
            onClick={(): void => onToggleCardType(VirtualCardTypes.standard)}
            isSelected={
              selectedCardTypes?.has(VirtualCardTypes.standard) || false
            }
          />
        )}
        {showRecurring && (
          <Checkbox
            label={translate('Recurring')}
            testId="recurringChk"
            onClick={(): void => onToggleCardType(VirtualCardTypes.recurring)}
            isSelected={
              selectedCardTypes?.has(VirtualCardTypes.recurring) || false
            }
          />
        )}
        {showGuestCard && (
          <Checkbox
            label={translate('Guest Card')}
            testId="guestChk"
            onClick={(): void => onToggleCardType(VirtualCardTypes.guestAccess)}
            isSelected={
              selectedCardTypes?.has(VirtualCardTypes.guestAccess) || false
            }
          />
        )}
        {showGuestRecurring && (
          <Checkbox
            label={translate('Guest Card Recurring')}
            testId="guestRecurringChk"
            onClick={(): void =>
              onToggleCardType(VirtualCardTypes.recurringGuest)
            }
            isSelected={
              selectedCardTypes?.has(VirtualCardTypes.recurringGuest) || false
            }
          />
        )}
        {showDecliningBalance && (
          <Checkbox
            label={translate('Declining Balance')}
            testId="decliningBalanceChk"
            onClick={(): void =>
              onToggleCardType(VirtualCardTypes.decliningBalance)
            }
            isSelected={
              selectedCardTypes?.has(VirtualCardTypes.decliningBalance) || false
            }
          />
        )}
        {showIssuerCycle && (
          <Checkbox
            label={translate('Recurring')}
            testId="issuerCycleChk"
            onClick={(): void => onToggleCardType(VirtualCardTypes.issuerCycle)}
            isSelected={
              selectedCardTypes?.has(VirtualCardTypes.issuerCycle) || false
            }
          />
        )}
      </div>
      <DividerLineDiv
        css={css`
          margin: 20px 0px;
        `}
      />
    </>
  )
}

export default VirtualCardTypeFilter
